import type { CSSProperties, ReactNode } from 'react';
import { useAssetUrl } from '../lib/useAssetUrl';

/**
 * Safe markdown → React elements for agent-authored text (feed bodies,
 * approval summaries, standups). Never touches dangerouslySetInnerHTML —
 * everything is built as React nodes, so model output can't inject HTML.
 *
 * Supported: **bold**, *italic* / _italic_, `code`, ``` fences, # ## ###
 * headings (rendered as modest strong-weight lines, not giant h1s),
 * - / * bullets, 1. numbered lists, > blockquotes, [text](url) links
 * (http/https only), paragraphs + line breaks, and optional @mention
 * highlighting (same regex + styling the Feed used).
 *
 * Sizing: everything is em-based and inherits the container font, so the
 * component reads correctly at feed (13.5px), small (12.5px) or body size.
 */

interface MarkdownProps {
  text?: string | null;
  /** Highlight @mentions (Feed behavior): ice + 600 weight. */
  mentions?: boolean;
  /**
   * Inline label rendered at the start of the first paragraph, e.g. "Win · ".
   * Lets call sites keep their `Label · body` pattern when the body is markdown.
   */
  prefix?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

/* ---------------------------------------------------------------- styles */

const S: Record<string, CSSProperties> = {
  root: { wordBreak: 'break-word', minWidth: 0 },
  block: { marginTop: 6 },
  h1: { display: 'block', fontSize: '1.12em', fontWeight: 650, color: 'var(--text)', letterSpacing: '-.01em' },
  h2: { display: 'block', fontSize: '1.06em', fontWeight: 650, color: 'var(--text)', letterSpacing: '-.01em' },
  h3: { display: 'block', fontSize: '1em', fontWeight: 600, color: 'var(--text)' },
  codeInline: {
    fontFamily: 'var(--mono)',
    fontSize: '0.9em',
    background: 'rgba(var(--ice-rgb),.08)',
    border: '1px solid var(--line)',
    borderRadius: 5,
    padding: '1px 5px',
    color: 'var(--text)',
    whiteSpace: 'pre-wrap',
  },
  pre: {
    fontFamily: 'var(--mono)',
    fontSize: '0.9em',
    lineHeight: 1.55,
    background: 'rgba(var(--ice-rgb),.05)',
    border: '1px solid var(--line)',
    borderRadius: 'var(--r-ctl)',
    padding: '10px 12px',
    overflowX: 'auto',
    whiteSpace: 'pre',
    color: 'var(--text)',
  },
  ul: { margin: 0, paddingLeft: 20, listStyle: 'disc', display: 'flex', flexDirection: 'column', gap: 2 },
  ol: { margin: 0, paddingLeft: 22, listStyle: 'decimal', display: 'flex', flexDirection: 'column', gap: 2 },
  quote: { margin: 0, borderLeft: '2px solid var(--line-2)', paddingLeft: 10, color: 'var(--dim)' },
  mention: { color: 'var(--ice)', fontWeight: 600 },
  bold: { fontWeight: 650, color: 'var(--text)' },
};

/* ------------------------------------------------------------ inline pass */

/** Same mention shape Feed.tsx used: /(@[a-zA-Z][a-zA-Z0-9_]*)/ */
const MENTION_RE = /@[a-zA-Z][a-zA-Z0-9_]*/;
const CODE_RE = /`([^`\n]+)`/;
const IMAGE_RE = /!\[([^\]\n]*)\]\(([^)\s]+)\)/;
const LINK_RE = /\[([^\]\n]+)\]\(([^)\s]+)\)/;

const imgStyle: CSSProperties = {
  display: 'block',
  maxWidth: '100%',
  borderRadius: 8,
  border: '1px solid var(--line)',
  margin: '6px 0',
};

/**
 * Inline markdown image. https URLs render directly; anything else is treated
 * as an R2 asset key (the drawer's image upload inserts keys, not URLs, so the
 * markdown never embeds an expiring token).
 */
function MdImage({ src, alt }: { src: string; alt: string }) {
  const isUrl = /^https?:\/\//i.test(src);
  const assetSrc = useAssetUrl(isUrl ? null : src);
  const url = isUrl ? src : assetSrc;
  if (!url) return <span style={{ ...imgStyle, height: 48, background: 'var(--panel-2)' }} aria-hidden />;
  return <img src={url} alt={alt} loading="lazy" style={imgStyle} />;
}
const BOLD_RE = /\*\*([^\n]+?)\*\*/;
const ITALIC_STAR_RE = /\*([^*\n]+)\*/;
const ITALIC_UNDER_RE = /(?<![\w_])_([^_\n]+)_(?![\w_])/; // skips snake_case

interface Found {
  index: number;
  length: number;
  render: (key: number) => ReactNode;
}

/** Earliest inline token in `rest`; ties go to the first pattern considered. */
function earliest(rest: string, mentions: boolean): Found | null {
  let best: Found | null = null;
  const consider = (f: Found | null) => {
    if (f && (best === null || f.index < best.index)) best = f;
  };

  const c = CODE_RE.exec(rest);
  if (c) {
    const body = c[1];
    consider({
      index: c.index,
      length: c[0].length,
      render: (k) => (
        <code key={k} style={S.codeInline}>
          {body}
        </code>
      ),
    });
  }

  const im = IMAGE_RE.exec(rest);
  if (im) {
    const alt = im[1];
    const src = im[2];
    // https URLs and asset keys render; unknown schemes stay literal text.
    if (/^https?:\/\//i.test(src) || !/^[a-z][a-z0-9+.-]*:/i.test(src)) {
      consider({
        index: im.index,
        length: im[0].length,
        render: (k) => <MdImage key={k} src={src} alt={alt} />,
      });
    } else {
      const raw = im[0];
      consider({ index: im.index, length: raw.length, render: () => raw });
    }
  }

  const l = LINK_RE.exec(rest);
  if (l) {
    const label = l[1];
    const url = l[2];
    if (/^https?:\/\//i.test(url)) {
      consider({
        index: l.index,
        length: l[0].length,
        render: (k) => (
          <a key={k} href={url} target="_blank" rel="noopener noreferrer">
            {parseInline(label, mentions)}
          </a>
        ),
      });
    } else {
      // Unsafe/unknown scheme: keep the literal text, never a live link.
      const raw = l[0];
      consider({ index: l.index, length: raw.length, render: () => raw });
    }
  }

  const b = BOLD_RE.exec(rest);
  if (b) {
    const inner = b[1];
    consider({
      index: b.index,
      length: b[0].length,
      render: (k) => (
        <strong key={k} style={S.bold}>
          {parseInline(inner, mentions)}
        </strong>
      ),
    });
  }

  const is = ITALIC_STAR_RE.exec(rest);
  if (is) {
    const inner = is[1];
    consider({
      index: is.index,
      length: is[0].length,
      render: (k) => <em key={k}>{parseInline(inner, mentions)}</em>,
    });
  }

  const iu = ITALIC_UNDER_RE.exec(rest);
  if (iu) {
    const inner = iu[1];
    consider({
      index: iu.index,
      length: iu[0].length,
      render: (k) => <em key={k}>{parseInline(inner, mentions)}</em>,
    });
  }

  if (mentions) {
    const m = MENTION_RE.exec(rest);
    if (m) {
      const at = m[0];
      consider({
        index: m.index,
        length: at.length,
        render: (k) => (
          <span key={k} style={S.mention}>
            {at}
          </span>
        ),
      });
    }
  }

  return best;
}

function parseInline(src: string, mentions: boolean): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = src;
  let k = 0;
  // Hard cap so a pathological string can never loop forever.
  let guard = 0;
  while (rest.length > 0 && guard++ < 1000) {
    const hit = earliest(rest, mentions);
    if (!hit) {
      out.push(rest);
      break;
    }
    if (hit.index > 0) out.push(rest.slice(0, hit.index));
    out.push(hit.render(k++));
    rest = rest.slice(hit.index + hit.length);
  }
  if (guard >= 1000 && rest.length > 0) out.push(rest);
  return out;
}

/** Paragraph/blockquote lines: inline-parse each, single newline → <br>. */
function renderLines(lines: string[], mentions: boolean): ReactNode[] {
  return lines.flatMap((ln, i) => {
    const span = <span key={`l${i}`}>{parseInline(ln, mentions)}</span>;
    return i === 0 ? [span] : [<br key={`b${i}`} />, span];
  });
}

/* ------------------------------------------------------------- block pass */

type Block =
  | { t: 'p'; lines: string[] }
  | { t: 'h'; level: 1 | 2 | 3; text: string }
  | { t: 'ul'; items: string[] }
  | { t: 'ol'; items: string[]; start: number }
  | { t: 'q'; lines: string[] }
  | { t: 'code'; body: string };

const FENCE_RE = /^\s*```(\S*)\s*$/;
const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const UL_RE = /^\s{0,3}[-*]\s+(.*)$/;
const OL_RE = /^\s{0,3}(\d+)[.)]\s+(.*)$/;
const QUOTE_RE = /^\s{0,3}>\s?(.*)$/;

function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length) {
      blocks.push({ t: 'p', lines: para });
      para = [];
    }
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (FENCE_RE.test(line)) {
      flush();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) {
        buf.push(lines[i]);
        i++;
      }
      i++; // past closing fence (or EOF)
      blocks.push({ t: 'code', body: buf.join('\n') });
      continue;
    }

    const h = HEADING_RE.exec(line);
    if (h) {
      flush();
      blocks.push({ t: 'h', level: Math.min(h[1].length, 3) as 1 | 2 | 3, text: h[2].trim() });
      i++;
      continue;
    }

    if (UL_RE.test(line)) {
      flush();
      const items: string[] = [];
      let m: RegExpExecArray | null;
      while (i < lines.length && (m = UL_RE.exec(lines[i]))) {
        items.push(m[1]);
        i++;
      }
      blocks.push({ t: 'ul', items });
      continue;
    }

    if (OL_RE.test(line)) {
      flush();
      const items: string[] = [];
      let start = 1;
      let m: RegExpExecArray | null;
      let firstItem = true;
      while (i < lines.length && (m = OL_RE.exec(lines[i]))) {
        if (firstItem) {
          const n = parseInt(m[1], 10);
          if (Number.isFinite(n)) start = n;
          firstItem = false;
        }
        items.push(m[2]);
        i++;
      }
      blocks.push({ t: 'ol', items, start });
      continue;
    }

    if (QUOTE_RE.test(line)) {
      flush();
      const qlines: string[] = [];
      let m: RegExpExecArray | null;
      while (i < lines.length && (m = QUOTE_RE.exec(lines[i]))) {
        qlines.push(m[1]);
        i++;
      }
      blocks.push({ t: 'q', lines: qlines });
      continue;
    }

    if (!line.trim()) {
      flush();
      i++;
      continue;
    }

    para.push(line);
    i++;
  }
  flush();
  return blocks;
}

/* --------------------------------------------------------------- renderer */

function renderBlock(b: Block, key: number, mentions: boolean, prefix?: ReactNode): ReactNode {
  const mt = key > 0 ? S.block : undefined;
  switch (b.t) {
    case 'p':
      return (
        <div key={key} style={mt}>
          {prefix}
          {renderLines(b.lines, mentions)}
        </div>
      );
    case 'h': {
      const hs = b.level === 1 ? S.h1 : b.level === 2 ? S.h2 : S.h3;
      return (
        <strong key={key} style={{ ...hs, ...mt }}>
          {parseInline(b.text, mentions)}
        </strong>
      );
    }
    case 'ul':
      return (
        <ul key={key} style={{ ...S.ul, ...mt }}>
          {b.items.map((it, i) => (
            <li key={i}>{parseInline(it, mentions)}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={key} start={b.start} style={{ ...S.ol, ...mt }}>
          {b.items.map((it, i) => (
            <li key={i}>{parseInline(it, mentions)}</li>
          ))}
        </ol>
      );
    case 'q':
      return (
        <blockquote key={key} style={{ ...S.quote, ...mt }}>
          {renderLines(b.lines, mentions)}
        </blockquote>
      );
    case 'code':
      return (
        <pre key={key} style={{ ...S.pre, ...mt }}>
          <code style={{ fontFamily: 'inherit' }}>{b.body}</code>
        </pre>
      );
  }
}

export function Markdown({ text, mentions = false, prefix, className, style }: MarkdownProps) {
  const src = (text ?? '').replace(/\s+$/, '');
  if (!src && prefix == null) return null;

  const blocks = parseBlocks(src);
  const prefixInline = prefix != null && blocks[0]?.t === 'p';

  return (
    <div className={className} style={{ ...S.root, ...style }}>
      {prefix != null && !prefixInline && <div>{prefix}</div>}
      {blocks.map((b, i) => renderBlock(b, i, mentions, i === 0 && prefixInline ? prefix : undefined))}
    </div>
  );
}
